/**
 * G4. Первая любовь 15–25 (при событии `love.first`): «письмо/сообщение».
 * Собрать фразу из трёх частей (обращение, суть, подпись) по четыре варианта
 * в каждой. «Правильного» нет: у партнёра скрытый профиль из жребия
 * (`state.profile`, по варианту на часть), совпадения 0…3 дают отношения
 * +2/+3/+5/+6. При отношениях ≥ FIRST_LOVE_HINT_RELATIONS одна часть профиля
 * подсвечивается (`state.hint`). «Пропустить» — одно совпадение (+3).
 */
import type { Dataset, GamblingLaw } from '@life/data';
import type { Life, MinigameOutcome, Rng } from '@life/engine';
import type { MinigameDef } from '../types';
import { nothing } from './common';

export const FIRST_LOVE_PARTS = 3;
export const FIRST_LOVE_OPTIONS = 4;
export const FIRST_LOVE_PART_IDS = ['greeting', 'core', 'signature'] as const;
/** Отношения по числу совпадений с профилем партнёра (0…3). */
export const FIRST_LOVE_RELATIONS_BY_MATCH = [2, 3, 5, 6] as const;
export const FIRST_LOVE_HINT_RELATIONS = 50;
/** Совпадений при «пропустить». */
export const FIRST_LOVE_SKIP_MATCHES = 1;

export interface FirstLoveState {
  /** Ключи вариантов по частям: `minigame.firstLove.<part>.<i>`. */
  parts: string[][];
  /** Скрытый профиль партнёра: индекс варианта по каждой части. */
  profile: number[];
  /** Подсказка: одна известная часть профиля, или null. */
  hint: { part: number; option: number } | null;
}

/** Выбранный вариант по каждой части. */
export type FirstLoveInput = { parts: number[] };

export function firstLoveOptionKey(part: number, option: number): string {
  return `minigame.firstLove.${FIRST_LOVE_PART_IDS[part] ?? part}.${option}`;
}

function result(matches: number): MinigameOutcome {
  const m = Math.min(FIRST_LOVE_PARTS, Math.max(0, matches));
  return { ...nothing(), stats: { relations: FIRST_LOVE_RELATIONS_BY_MATCH[m] as number }, logKey: `minigame.firstLove.match.${m}`, logParams: { matches: m } };
}

export const firstLove: MinigameDef<FirstLoveInput, FirstLoveState> = {
  id: 'firstLove',
  section: 'G',
  triggers: [{ kind: 'event', event: 'love.first' }],
  available(_life: Life, _ds: Dataset, _law: GamblingLaw): boolean {
    return true;
  },
  setup(life: Life, _ds: Dataset, rng: Rng): FirstLoveState {
    const parts: string[][] = [];
    const profile: number[] = [];
    for (let p = 0; p < FIRST_LOVE_PARTS; p += 1) {
      const options: string[] = [];
      for (let i = 0; i < FIRST_LOVE_OPTIONS; i += 1) options.push(firstLoveOptionKey(p, i));
      parts.push(options);
      profile.push(rng.int(0, FIRST_LOVE_OPTIONS - 1));
    }
    let hint: FirstLoveState['hint'] = null;
    if (life.stats.relations >= FIRST_LOVE_HINT_RELATIONS) {
      const part = rng.int(0, FIRST_LOVE_PARTS - 1);
      hint = { part, option: profile[part] as number };
    }
    return { parts, profile, hint };
  },
  play(state: FirstLoveState, input: FirstLoveInput, _life: Life): MinigameOutcome {
    const picks = Array.isArray(input.parts) ? input.parts : [];
    let matches = 0;
    for (let p = 0; p < state.profile.length; p += 1) if (picks[p] === state.profile[p]) matches += 1;
    return result(matches);
  },
  expected(): MinigameOutcome {
    return result(FIRST_LOVE_SKIP_MATCHES);
  },
};
